import { useSuspenseQuery } from '@apollo/client'
import {
  FetchOrganizationSettingDocument,
  type GoalConfigFieldsFragment,
  type ViewFieldsFragment,
} from '@salescore/client-api'
import { getOrganizationIdFromPath, useCanForView } from '@salescore/client-common'
import { type CachedView, selectCurrentCachedView, useAppSelector } from '@salescore/client-redux'
import {
  CORE_CONSTANT,
  type GoalConfig,
  type ViewConfig,
  type ViewConfigKpiParameter,
  type ViewConfigKpiPivot,
  type ViewConfigKpiPreset,
} from '@salescore/core'
import { isSharedLinkPresetName } from '@salescore/core'
import { NotionLikeSelector, removeQueryParameterFromUrl } from '@salescore/frontend-common'
import { App, Button } from 'antd'
import { Array } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactElement, useEffect, useMemo } from 'react'
import { useRecoilState, useSetRecoilState } from 'recoil'

import { kpiParameterAtom, kpiPivotPickedPresetNameAtom } from '../../../recoil/navigation/atoms'
import { configAtom } from '../../../recoil/viewConfig'
import { getKpiPresetParameterKeysWithValues, useSetPresetToConfig } from './KpiPresetForm'
import { buildKpiPresetFormModalProperties } from './KpiPresetFormModal'

export const KpiPivotPresetsPicker = ({
  canUpdate,
  withNoDateFieldKpis,
  view,
  config,
  hasChangeToKpiPivotParameter,
  setHasChangeToViewSchema,
  goalConfigs,
}: {
  canUpdate: boolean
  withNoDateFieldKpis: boolean
  view: ViewFieldsFragment
  config: ViewConfigKpiPivot
  hasChangeToKpiPivotParameter: boolean
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
  goalConfigs: GoalConfigFieldsFragment[]
}): ReactElement => {
  const setConfig = useSetRecoilState(configAtom)
  const [picked, setPicked] = useRecoilState(kpiPivotPickedPresetNameAtom)
  const [parameter, setParameter] = useRecoilState(kpiParameterAtom)
  const setPreset = useSetPresetToConfig()
  const { modal, message } = App.useApp()
  const currentCachedView = useAppSelector((state) => selectCurrentCachedView(state, { viewId: view.id }))
  const canDisplayConfigEdit = useCanForView('display-config-edit', view)
  const { data: organizationSetting } = useSuspenseQuery(FetchOrganizationSettingDocument, {
    variables: { organizationId: getOrganizationIdFromPath() },
  })

  // presetにある組織の階層構造名や目標(goalConfig)が古い場合があるため、最新の設定内容で上書きする
  const presets = useMemo(() => {
    const userGroupNames = organizationSetting.organization.setting.userGroupCategoryNames.map((value, index) => {
      const groupDimensionKey = `p_salescore_user_groups_d${index + 1}_id`
      return { groupDimensionKey, name: value }
    })
    const updatedPresets = config.presets?.map((preset): ViewConfigKpiPreset => {
      // userGroupに関連するラベル名の更新
      const newRows = Array.flatMapNullishOr(preset.parameter.pivot.rows, (row) => {
        const latestLabel = row.key.startsWith(`p_salescore_user_groups`)
          ? // eslint-disable-next-line max-nested-callbacks
            userGroupNames.find((x) => x.groupDimensionKey === row.key)?.name
          : row.label
        // presetにある階層が存在しない場合は表示しない
        if (latestLabel === undefined) {
          return
        }
        return {
          ...row,
          label: latestLabel,
        }
      })
      return {
        ...preset,
        parameter: {
          ...preset.parameter,
          pivot: {
            ...preset.parameter.pivot,
            rows: newRows,
          },
          // XXX: プリセットの中に保存されているgoalConfigが古い可能性があるので、ここで最新にする
          //      そもそもプリセットにはgoalConfigのIDのみを保持すべきで、ロジックがやや破綻している
          goalConfig: goalConfigs.find((x) => x.id === preset.parameter.goalConfig?.id),
        },
      }
    })

    return Array.isArray(updatedPresets) && updatedPresets.length > 0 ? updatedPresets : []
  }, [config.presets, organizationSetting, goalConfigs])

  const select = (value: string): void => {
    const newPreset = presets.find((x) => x.name === value)
    if (newPreset !== undefined) {
      // プリセットが[共有リンク]以外になった際に URL から shareId を除去 (reload での再注入を防ぐ)。
      // redux の shareId は残す — dispatch すると useSharedUrl (Suspense query) が再発火し画面がちらつくため
      if (!isSharedLinkPresetName(value)) {
        removeQueryParameterFromUrl('shareId')
      }
      setPicked(newPreset.name)
      setParameter(newPreset.parameter)
    }
  }

  const pickedPreset = presets.find((x) => x.name === picked)
  useEffect(() => {
    if (pickedPreset === undefined) {
      setInitPreset({
        config,
        currentCachedView,
        presets,
        goalConfigs,
        setPicked,
        setParameter,
      })
    } else {
      changePreset({
        pickedPreset,
        currentCachedView,
        withNoDateFieldKpis,
        setParameter,
      })
    }
    // eslint-disable-next-line sonarjs/todo-tag
  }, [picked]) // TODO: setInitPreset の後に changePreset が呼ばれないとうまくいっていなかった。ここは空にしたい。

  return (
    <NotionLikeSelector
      disableAddition={!canUpdate || !canDisplayConfigEdit}
      showAddition={true}
      className="mr-2"
      buttonProps={{ type: 'text' }}
      value={pickedPreset?.name}
      uneditable={isSharedLinkPresetName}
      options={presets.map((x) => ({ value: x.name, label: x.name, disableDrag: false }))}
      setValue={(value) => {
        if (hasChangeToKpiPivotParameter) {
          const messageKey = 'select-preset-warning'
          message.warning({
            key: messageKey,
            content: (
              <span>
                {t(`変更差分がある場合はプリセットの変更により差分が削除される可能性があります。`)}
                <br />
                {t(`プリセットを変更しますか？`)}
                <Button
                  className="ml-2"
                  onClick={() => {
                    select(value)
                    message.destroy(messageKey)
                  }}
                >
                  {t(`はい`)}
                </Button>
              </span>
            ),
            duration: 3,
          })
        } else {
          select(value)
        }
      }}
      placeholder={t(`プリセット`)}
      additionText={t(`プリセットの新規作成`)}
      onAddButtonClick={() => {
        const m = modal.confirm(
          buildKpiPresetFormModalProperties({
            presets,
            // 新規作成は表示中の集計条件 (kpiParameterAtom) が値を持つキーを初期選択にする (本 MR 前と同じ式)
            initialSelectedKeys: getKpiPresetParameterKeysWithValues(parameter),
            onFinish: (newPreset) => {
              setPreset(newPreset)
              setHasChangeToViewSchema(true)
              void message.success(t('集計プリセットを保存しました'))
              m.destroy()
            },
          }),
        )
      }}
      // 一覧の表示には正規化済みの presets を使うが、config.presets へ書き戻す 3 経路 (編集・並び替え・削除) は
      // 保存済みの値から引く。正規化は最新の階層名・目標を当てる表示用の変換で、そのまま書き戻すと
      // 参照先が消えた目標や存在しない階層行が落ちた状態で永続化される
      onEdit={
        canUpdate && canDisplayConfigEdit
          ? (value) => {
              const preset = config.presets?.find((x) => x.name === value)
              const m = modal.confirm(
                buildKpiPresetFormModalProperties({
                  preset,
                  presets,
                  // 鉛筆経由は初期選択を空にする。選んだ設定だけを表示中の値で上書きし、
                  // 名称だけ変えた保存で他の設定が置き換わらないようにする
                  initialSelectedKeys: [],
                  onFinish: (newPreset) => {
                    setPreset(newPreset, preset)
                    setHasChangeToViewSchema(true)
                    void message.success(t('集計プリセットを保存しました'))
                    m.destroy()
                  },
                }),
              )
            }
          : undefined
      }
      onSort={
        canUpdate && canDisplayConfigEdit
          ? (values: string[]) => {
              const sortedPresets = values
                .map((value) => config.presets?.find((x) => x.name === value))
                .filter(isNotNullish)
              setConfig({
                ...config,
                presets: sortedPresets,
              })
              setHasChangeToViewSchema(true)
            }
          : undefined
      }
      onDestroy={
        canUpdate && canDisplayConfigEdit
          ? (value: string): void => {
              // [共有リンク]を削除する場合は shareId を URL から取り除く
              if (isSharedLinkPresetName(value)) {
                removeQueryParameterFromUrl('shareId')
              }
              const newPresets = config.presets?.filter((x) => x.name !== value) ?? []
              setConfig({
                ...config,
                presets: newPresets,
              })
              setHasChangeToViewSchema(true)
              // 選択中プリセットが削除された場合は先頭のプリセットをデフォルトとして設定する
              if (value === picked && newPresets[0]?.name !== undefined) {
                select(newPresets[0].name)
              }
            }
          : undefined
      }
    />
  )
}

function changePreset({
  currentCachedView,
  pickedPreset,
  withNoDateFieldKpis,
  setParameter,
}: {
  currentCachedView?: CachedView
  pickedPreset: ViewConfigKpiPreset
  withNoDateFieldKpis: boolean
  setParameter: (kpiParameter: ViewConfigKpiParameter) => void
}) {
  // キャッシュが存在する場合はキャッシュを利用する
  // kpiParameterのRecoilへのセットはinitializeStateで行なっている
  if (
    currentCachedView?.kpiPivotPickedPresetName === pickedPreset.name &&
    currentCachedView.additionalConfig !== undefined
  ) {
    return
  }

  setParameter({
    ...pickedPreset.parameter,
    // KPIに期間項目が含まれない場合は期間を考慮しないため、Periodを明示的に全期間にする
    queryParameter: {
      ...pickedPreset.parameter.queryParameter,
      periodName: withNoDateFieldKpis ? '全期間' : pickedPreset.parameter.queryParameter?.periodName,
      startAt: withNoDateFieldKpis ? undefined : pickedPreset.parameter.queryParameter?.startAt,
      endAt: withNoDateFieldKpis ? undefined : pickedPreset.parameter.queryParameter?.endAt,
    },
  })
}

function setInitPreset({
  config,
  currentCachedView,
  presets,
  goalConfigs,
  setPicked,
  setParameter,
}: {
  config: ViewConfig
  currentCachedView?: CachedView
  presets: ViewConfigKpiPreset[]
  goalConfigs: GoalConfig[]
  setPicked: (pickedPresetName: string) => void
  setParameter: (kpiParameter: ViewConfigKpiParameter) => void
}) {
  // キャッシュが存在する場合は、キャッシュを利用する
  // kpiParameterのRecoilへのセットはinitializeStateで行なっている
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: 初期化処理を1つにまとめたい。。。
  if (currentCachedView?.additionalConfig !== undefined) {
    return
  }

  // プリセットが未選択なとき、先頭のものを強制的に選択する
  // goalConfigについて、同上
  const [firstPreset] = presets
  if (firstPreset !== undefined) {
    setPicked(firstPreset.name)
    setParameter(firstPreset.parameter)
    return
  }

  if (config.type === 'kpiPivot') {
    setParameter({
      ...CORE_CONSTANT.KPI_PIVOT_DEFAULT_PRESET().parameter,
      goalConfig: goalConfigs[0],
    })
  } else {
    setParameter({
      ...CORE_CONSTANT.PIVOT_EMPTY_PRESET().parameter,
      goalConfig: goalConfigs[0],
    })
  }
}
